"use client";

import { type HTMLAttributes } from "react";
import { motion } from "framer-motion";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "@/util";
import { Stars } from "./stars";

export const cardVariants = cva(
  "relative overflow-hidden border p-1 rounded-2xl bg-black border-white/20 shadow-inner",
  {
    variants: {
      variant: {
        primary: "bg-black/20 backdrop-blur-md shadow-lg shadow-black/20",
        secondary:
          "bg-[#161621]/30 backdrop-blur-[50px] border-white/10 shadow-[0_4px_54px_rgba(0,0,0,0.25)]",
      },
    },
    defaultVariants: {
      variant: "primary",
    },
  }
);

export interface CardProps
  extends HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof cardVariants> {
  stars?: boolean;
}

export const Card = ({
  children,
  variant,
  className,
  stars,
  ...props
}: CardProps) => (
  <div
    className={cn(
      cardVariants({ variant }),
      "relative overflow-hidden",
      className
    )}
    {...props}
  >
    <div className="relative z-10">{children}</div>
    {stars ? (
      <>
        <Stars className="absolute inset-0 rounded-lg" animation />
        <AnimatedShape className="absolute bottom-0 left-0 w-full h-24" />
      </>
    ) : null}
  </div>
);

interface AnimatedShapeProps {
  src?: string;
  alt?: string;
  className?: string;
}

export const AnimatedShape = ({
  src = "/glow-card.svg",
  alt = "Shape",
  className,
}: AnimatedShapeProps) => (
  <motion.div
    className={cn("overflow-hidden", className, "h-96 w-full")}
    style={{ willChange: "transform" }}
    animate={{
      rotate: [0, 0, 0, 0],
      opacity: [0.1, 0.3, 0.8, 0.98, 0.7, 0.1],
    }}
    transition={{
      duration: 6,
      repeat: Infinity,
      repeatType: "loop",
      ease: "easeInOut",
    }}
  >
    <img
      src={src}
      alt={alt}
      className="absolute bottom-0 -z-10 w-full h-full object-cover"
      style={{ borderRadius: "inherit" }}
    />
  </motion.div>
);
